import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from 'hooks/useAuth';
import { sendErrorToSentry } from 'utils/sentryHelpers';
import jwtDecode from 'jwt-decode';
import { handleUnauthenticated } from 'utils/authHelpers';
import { useHistory } from 'react-router-dom';

export const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];
export const ACTIVITY_COOLDOWN = 60000; // 1 minute
export const ACTIVITY_CHECK_INTERVAL = 10000; // 10 seconds

const ActivityMonitor = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth();
  const history = useHistory();
  const lastActivity = useRef(0);
  const lastRenewal = useRef(0);
  const lastExpiry = useRef(Infinity);

  const handleTokenRenewal = useCallback(async () => {
    const now = Date.now();
    const timeSinceRenewal = now - lastRenewal.current;
    const hasNewActivity = lastActivity.current > lastRenewal.current;

    if (now >= lastExpiry.current) {
      handleUnauthenticated(loginWithRedirect, history);
      return;
    }

    if (timeSinceRenewal >= ACTIVITY_COOLDOWN && hasNewActivity) {
      try {
        // This will always renew the Access Token and cache it.
        const token = await getAccessTokenSilently({ cacheMode: 'off' });

        // Note: jwtDecode DOES NOT validate the token. It only decodes it.
        const decodedToken = jwtDecode(token);

        lastRenewal.current = now;
        // Note: The exp field in the JWT token does not include milliseconds,
        // we adjust it here so it can be compared with Date.now() later.
        lastExpiry.current = decodedToken.exp * 1000;
      } catch (error) {
        sendErrorToSentry(error);
      }
    }
  }, [getAccessTokenSilently, loginWithRedirect, history]);

  const handleUserActivity = useCallback(() => {
    lastActivity.current = Date.now();
  }, []);

  useEffect(() => {
    let intervalId = null;

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    // This WILL NOT renew the Access Token and will fail if it is not in the cache.
    // It should be safe to do this here, because RequireAuth runs before, always,
    // forcing the user to log in and populating the cache.
    getAccessTokenSilently({ cacheMode: 'cache-only' })
      .then((token) => {
        const decodedToken = jwtDecode(token);
        lastExpiry.current = decodedToken.exp * 1000;
      })
      .catch(sendErrorToSentry)
      .finally(() => {
        intervalId = setInterval(handleTokenRenewal, ACTIVITY_CHECK_INTERVAL);
      });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
      clearInterval(intervalId);
    };
  }, [
    getAccessTokenSilently,
    loginWithRedirect,
    history,
    handleUserActivity,
    handleTokenRenewal
  ]);

  return null;
};

export default ActivityMonitor;
